export type CollateralKey = "ICP" | "ckBTC" | "ckETH";

export const COLLATERALS: {
	key: CollateralKey;
	label: string;
	icon: string;
	price: number;
	maxLtv: number;
}[] = [
	{ key: "ICP", label: "ICP", icon: "/assets/tokens/icp.png", price: 12.4, maxLtv: 0.66 },
	{ key: "ckBTC", label: "ckBTC", icon: "/assets/tokens/ckbtc.png", price: 97500, maxLtv: 0.75 },
	{ key: "ckETH", label: "ckETH", icon: "/assets/tokens/cketh.png", price: 3420, maxLtv: 0.7 },
];

export const FIXTURE = {
	agentCount: 128,
	tvlUsd: 4_820_000,
	vaultCount: 312,
	borrowVolumeUsd: 1_940_000,
	peg: 1.0012,
	borrowApr: 3.2,
	prices: {
		ICP: 12.4,
		ckBTC: 97500,
		ckETH: 3420,
	} as Record<CollateralKey, number>,
	balances: {
		USDM: 1250.5,
		ICP: 420.0,
		ckBTC: 0.015,
		ckETH: 0.8,
		MMK: 5000,
		ckUSDC: 250,
	},
	vaults: Array.from({ length: 12 }, (_, i) => {
		const collaterals: CollateralKey[] = ["ICP", "ckBTC", "ckETH"];
		const c = collaterals[i % 3];
		const debt = 800 + i * 340;
		const colUsd = debt * (1.45 + (i % 5) * 0.08);
		const ltv = debt / colUsd;
		return {
			id: BigInt(1000 + i),
			owner: `aaaaa-aa${i.toString().padStart(2, "0")}-cai`,
			collateral: c,
			collateralUsd: colUsd,
			debtUsdm: debt,
			ltv,
			health: ltv > 0.85 ? ("at_risk" as const) : ("healthy" as const),
		};
	}),
	liquidatable: [
		{
			id: 1042n,
			collateral: "ICP" as CollateralKey,
			debtUsdm: 4200,
			bonusPct: 5.2,
			ltv: 0.92,
		},
		{
			id: 1088n,
			collateral: "ckETH" as CollateralKey,
			debtUsdm: 9100,
			bonusPct: 4.8,
			ltv: 0.9,
		},
		{
			id: 1101n,
			collateral: "ckBTC" as CollateralKey,
			debtUsdm: 15000,
			bonusPct: 6.1,
			ltv: 0.94,
		},
	],
	auctions: [
		{ id: "A-19", vaultId: 991n, remainingUsdm: 2200, discountPct: 3.5 },
		{ id: "A-20", vaultId: 1003n, remainingUsdm: 800, discountPct: 7.0 },
	],
	proposals: [
		{
			id: 41,
			title: "Raise ckBTC debt ceiling",
			status: "active" as const,
			forVotes: 1_200_000,
			againstVotes: 80_000,
			description: "Increase ckBTC vault debt ceiling by 15% to meet demand.",
		},
		{
			id: 40,
			title: "Lower stability fee to 3.0%",
			status: "active" as const,
			forVotes: 900_000,
			againstVotes: 210_000,
			description: "Reduce borrow APR target from 3.2% to 3.0%.",
		},
		{
			id: 39,
			title: "Pause PegSwap emergency flag",
			status: "passed" as const,
			forVotes: 2_100_000,
			againstVotes: 12_000,
			description: "Timelocked pause for PegSwap during oracle maintenance.",
		},
	],
	treasury: [
		{ label: "Ops ICP", amount: "48,200 ICP", note: "Gas / redeem sleeve" },
		{ label: "Treasury MMK", amount: "2.1M MMK", note: "Non-voting ops" },
		{ label: "Seized ckBTC", amount: "1.24", note: "Protocol-owned" },
	],
	intelligenceFaqs: [
		"What is Motoko Dollar ($USDM)?",
		"How do I mint USDM against ICP?",
		"What collateral is supported?",
		"How do liquidations work?",
		"What is MotokoMaker ($MMK)?",
		"How does PegSwap keep the peg?",
	],
	/** Share of vault TVL by collateral (offline analytics). */
	collateralMix: [
		{ asset: "ICP" as CollateralKey, sharePct: 45, lockedUsd: 4_820_000 * 0.45 },
		{ asset: "ckBTC" as CollateralKey, sharePct: 35, lockedUsd: 4_820_000 * 0.35 },
		{ asset: "ckETH" as CollateralKey, sharePct: 20, lockedUsd: 4_820_000 * 0.2 },
	],
	atRiskCount: 18,
	savingsDeposited: 40_000,
	/** Last N daily TVL / debt points for sparkline / table. */
	analyticsHistory: Array.from({ length: 14 }, (_, i) => {
		const day = 13 - i;
		const t = Date.now() - day * 86_400_000;
		const drift = 1 + (i % 5) * 0.008 - 0.02;
		return {
			timestamp: BigInt(t) * 1_000_000n,
			tvl: Math.round(4_820_000 * drift),
			totalDebt: Math.round(1_940_000 * (0.96 + (i % 7) * 0.01)),
			activeVaults: 300 + i * 2,
		};
	}),
	collateralParams: [
		{
			asset: "ICP" as CollateralKey,
			maxLtv: 0.66,
			liqThreshold: 0.85,
			stabilityFeeBps: 320,
			debtCeiling: 5_000_000,
		},
		{
			asset: "ckBTC" as CollateralKey,
			maxLtv: 0.75,
			liqThreshold: 0.9,
			stabilityFeeBps: 280,
			debtCeiling: 8_000_000,
		},
		{
			asset: "ckETH" as CollateralKey,
			maxLtv: 0.7,
			liqThreshold: 0.88,
			stabilityFeeBps: 300,
			debtCeiling: 4_000_000,
		},
	],
};

export type VaultRow = (typeof FIXTURE.vaults)[number];
